import clsx from 'clsx';
import React from 'react';
import {twMerge} from 'tailwind-merge';

type Action = {
  icon: string;
  color: string;
  handler: (row: any) => void;
  permission: (row: any) => boolean;
  isShown?: (row: any) => boolean;
  render?: (row: any) => React.ReactNode;
};

type Column = {
  header: string;
  field: string;
  render?: (row: any) => React.ReactNode;
};

type DataTableBasicProps = {
  data: any[];
  columns: Column[];
  actions?: Action[];
  className?: string;
  title?: string;
  noResultsMessage?: string;
  // Display options
  raw?: boolean;
  hideTitle?: boolean;
  addButtonLabel?: string;
  hideAdd?: boolean;
  onAdd?: () => void;
};

export function DataTableBasic({
  data,
  columns,
  actions,
  className,
  title,
  noResultsMessage = 'No results found',
  raw = false,
  hideTitle = false,
  addButtonLabel = 'Create New',
  hideAdd = false,
  onAdd,
}: DataTableBasicProps) {
  // Default render function for columns that don't have a custom render function
  const defaultRender = (row: any, field: string) => {
    const fieldParts = field.split('.');
    let value = row;
    for (const part of fieldParts) {
      value = value[part];
      if (value === undefined) {
        return '';
      }
    }
    return String(value);
  };

  // Determine the maximum number of columns, including the action column if present
  const maxColumns =
    data.length === 0
      ? 1
      : Math.max(...columns.map(column => column.header.length)) + (actions && actions.length > 0 ? 1 : 0);
  const gridColsClass = `grid-cols-${Math.min(maxColumns, 12)}`; // Limiting to a maximum of 12 columns
  const maxWidthClass = 'max-w-xs';

  return (
    <div className={twMerge(clsx(`grid ${gridColsClass} gap-x-6`, className))}>
      <div className="col-span-12 xl:col-span-12">
        <div className="box">
          {!raw && !(hideTitle && hideAdd) && (
            <div className="box-header flex-wrap justify-between">
              {!hideTitle && title ? <h1 className="box-title">{title}</h1> : <div className="grow"></div>}
              <div className="flex items-stretch gap-2">
                {/* Add Button */}
                {!hideAdd && (
                  <div>
                    <button
                      onClick={onAdd}
                      className="ti-btn ti-btn-primary !m-0 h-full !gap-0 !bg-primary !px-2 !py-1 !text-[0.75rem] !font-medium !text-white"
                    >
                      {addButtonLabel}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
          <div className="box-body">
            <div className="table-responsive">
              <table className="table-hover table-bordered table min-w-full whitespace-nowrap">
                <thead>
                  <tr>
                    {columns.map((column, index) => (
                      <th key={index} className="text-start">
                        {column.header}
                      </th>
                    ))}
                    {actions && <th className="text-center">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {data.length === 0 ? (
                    <tr className="border">
                      <td colSpan={columns.length + (actions ? 1 : 0)}>
                        <div className="text-center font-bold">{noResultsMessage}</div>
                      </td>
                    </tr>
                  ) : (
                    data.map((row, rowIndex) => (
                      <tr
                        key={rowIndex}
                        className="border border-solid border-inherit hover:bg-gray-100 dark:border-defaultborder/10 dark:hover:bg-light"
                      >
                        {columns.map((column, colIndex) => (
                          <td key={colIndex} className={`overflow-hidden ${maxWidthClass}`}>
                            {column.render ? column.render(row) : defaultRender(row, column.field)}
                          </td>
                        ))}
                        {actions && actions.some(action => action.permission(row)) && (
                          <td>
                            <div className="flex flex-row items-center justify-center gap-2 text-[0.9375rem]">
                              {actions
                                .filter(action => action.isShown?.(row) ?? true)
                                .map(
                                  (action, actionIndex) =>
                                    action.permission(row) &&
                                    (action.render ? (
                                      <div key={actionIndex}>{action.render(row)}</div>
                                    ) : (
                                      <button
                                        key={actionIndex}
                                        onClick={() => action.handler(row)}
                                        className={`ti-btn ti-btn-icon ti-btn-wave !m-0 !h-[1.75rem] !w-[1.75rem] !gap-0 text-[0.8rem] bg-${action.color}/10 text-${action.color} hover:bg-${action.color} hover:text-white hover:border-${action.color}`}
                                      >
                                        <i className={action.icon}></i>
                                      </button>
                                    ))
                                )}
                            </div>
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
